import { Vue, Component } from "vue-facing-decorator";

@Component
export class CompanyFilterMixin extends Vue {
    /**
     * Generic method to filter active/inactive companies.
     * Using a generic type <T> instead of specific Company type provides flexibility,
     * allowing this method to work with any object that has an isActive property.
     * This preserves the original type and supports inheritance.
     *
     * @param allItems - Complete collection of items (both active and inactive)
     * @param activeItems - Collection containing only active items
     * @param showInactive - Flag to determine which collection to return
     * @returns Either all items or only active items based on showInactive flag and total count
     */
    protected getFilteredCompanies<T extends { isActive?: boolean }>(
        allItems: T[],
        activeItems: T[],
        showInactive: boolean,
    ): T[] {
        if (!allItems || allItems.length <= 5) {
            return allItems;
        }

        return showInactive ? allItems : activeItems;
    }

    /**
     * Helper method to prepare and organize company data by active status.
     * Uses generics to maintain type safety while being flexible enough to handle any
     * object type with an isActive property, not just the Company type.
     *
     * @param companies - Original collection of companies or similar objects
     * @returns Object containing both the complete sorted collection and filtered active items
     */
    protected prepareCompanies<T extends { isActive?: boolean }>(
        companies: T[],
    ): { allItems: T[]; activeItems: T[] } {
        if (!companies) {
            return { allItems: [], activeItems: [] };
        }

        const activeItems = companies.filter((company) => company.isActive !== false);

        const sortedCompanies = [
            ...activeItems,
            ...companies.filter((company) => company.isActive === false),
        ];

        return {
            allItems: sortedCompanies,
            activeItems: activeItems,
        };
    }
}
